import { gql } from "@apollo/client";

export const GetAllAssessments = gql`
  query GetAllAssessments {
    assessments {
      id
      order
      displayName
      timeToComplete
      assessmentGrouping {
        name
        order
        assessmentPart {
          name
          order
        }
      }
    }
  }
`;

export const AllAssessmentCycles = gql`
  query UserAssessmentCycles(
    $page: Int
    $limit: Int
    $searches: SearchObject
    $filters: [FilterObject!]
    $sort: OrderObject
  ) {
    userAssessmentCycles(
      page: $page
      limit: $limit
      searches: $searches
      filters: $filters
      sort: $sort
    ) {
      pageInfo {
        pageTotal
      }
      data {
        id
        cycleShortName
        cycleNumber
        status
        cycleCompletionDate
        lastCoachingSession {
          sessionTime
          coach {
            firstName
            lastName
          }
        }
        nextCoachingSession {
          sessionTime
          coach {
            firstName
            lastName
          }
        }
        user {
          id
          firstName
          lastName
          phone
          email
        }
        aceCompleted
      }
    }
  }
`;

export const GetTaskList = gql`
  query GetTaskList {
    taskList {
      id
      status
      statusDates {
        ready_to_score
      }
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

export const UserAssessmentCycleStatistics = gql`
  query UserAssessmentCycleStatistics {
    userAssessmentCycleStatistics {
      status
      count
      statisticLabel
      statisticValue
    }
  }
`;

export const AssignUserAssessmentCycle = gql`
  mutation AssignUserAssessmentCycle($input: AssignCycleInput!) {
    assignCycle(input: $input) {
      success
    }
  }
`;

export const ClaimUserAssessmentCycle = gql`
  mutation ClaimUserAssessmentCycle($id: String!) {
    claimCycle(id: $id) {
      success
    }
  }
`;

export const ReleaseUserAssessmentCycle = gql`
  mutation ReleaseUserAssessmentCycle($id: String!) {
    releaseCycle(id: $id) {
      success
    }
  }
`;

export const SubmitUserAssessmentCycleForReview = gql`
  mutation SubmitUserAssessmentCycleForReview($id: String!) {
    submitCycleForReview(id: $id) {
      success
    }
  }
`;

export const AllSurveys = gql`
  query Surveys {
    surveys {
      id
      name
    }
  }
`;

const userAssessmentFragment = gql`
  fragment userAssessment on UserAssessment {
    name
    id
    isCodingClinicianReviewed
    internalNotes {
      id
    }
    scoreRanges {
      scoreName
      underperforming
      competent
      accelerated
      reverse
    }
    scores {
      id
      value
      scoreType
      scoreName
      overrideScore
    }
    assessment {
      automatedScoring
      order
      displayName
      assessmentGrouping {
        order
        name
        assessmentPart {
          order
        }
      }
      pillarAssessments {
        id
        pillarName
      }
    }
  }
`;

export const GetAppropriateAssessmentCycleOverview = gql`
  query UserAssessmentCycle($id: ID!, $refactor: Boolean!) {
    userAssessmentCycle(id: $id) {
      id
      readyToFinalize
      status
      cycle {
        cycleNumber
        id
        interval
        organizationId
      }
      cycleCompletionDate
      pillars @skip(if: $refactor)
      bhiRefactorPillars @include(if: $refactor)
      bhiRefactorPillarScores @include(if: $refactor) {
        pillarName
        value
        userAssessments: bhiRefactorUserAssessments {
          ...userAssessment
        }
      }
      pillarScores @skip(if: $refactor) {
        pillarName
        value
        userAssessments {
          ...userAssessment
        }
      }
      user {
        id
        firstName
        lastName
        organization {
          id
          displayName
        }
      }
      userAssessments {
        ...userAssessment
      }
      claimUser {
        id
        firstName
        lastName
      }
      isClaimedByCurrentUser
      internalNotes {
        id
      }
      aceScores {
        id
        module
        value
        scoreRange {
          id
          min
          low
          high
          max
          reverse
          units
        }
      }
      userBrainHqAssessment {
        id
        userId
        userAssessmentCycleId
        completionDateTime
        skippedDateTime
        summaryStatistics
      }
    }
  }
  ${userAssessmentFragment}
`;

export const GetAssessmentCycleOverview = gql`
  query UserAssessmentCycle($id: ID!) {
    userAssessmentCycle(id: $id) {
      id
      userId
      readyToFinalize
      status
      cycle {
        id
        cycleNumber
      }
      cycleCompletionDate
      pillarScores {
        pillarName
        value
      }
      user {
        id
        firstName
        lastName
        organization {
          id
          displayName
        }
      }
      claimUser {
        id
        firstName
        lastName
      }
      isClaimedByCurrentUser
      internalNotes {
        id
      }
      aceScores {
        id
        module
        value
        scoreRange {
          id
          min
          low
          high
          max
          reverse
          units
        }
      }
      userAssessments {
        id
        internalNotes {
          id
        }
        scoreRanges {
          scoreName
          underperforming
          competent
          accelerated
          reverse
        }
        scores {
          id
          value
          scoreType
          scoreName
          overrideScore
        }
        assessment {
          automatedScoring
          order
          displayName
          assessmentGrouping {
            order
            name
            assessmentPart {
              order
            }
          }
          pillarAssessments {
            id
            pillarName
          }
        }
      }
    }
  }
`;

export const GetCycleStatus = gql`
  query UserAssessmentCycle($id: ID!) {
    userAssessmentCycle(id: $id) {
      status
      cycle {
        cycleNumber
      }
    }
  }
`;

export const GetUserAssessmentsNamesByCycle = gql`
  query UserAssessments($input: UserAssessmentQueryInput!) {
    userAssessments(input: $input) {
      id
      name
    }
  }
`;

export const SingleUserAssessment = gql`
  query SingleUserAssessment($id: ID!) {
    userAssessment(id: $id) {
      id
      userAssessmentCycle {
        status
        isClaimedByCurrentUser
      }
      validityCode
      isCodingClinicianReviewed
      user {
        id
        firstName
        lastName
      }
      memoryMetadata {
        id
        userAssessmentId
        userAnswerId
        data {
          confidence
          coach
          ml
        }
      }
      abstractionHighlights {
        id
        startIndex
        endIndex
        userAnswerId
        mlGenerated
        mlAccepted
        themeKey {
          id
          key
        }
      }
      reasoningHighlights {
        id
        startIndex
        endIndex
        userAnswerId
        mlGenerated
        mlAccepted
        themeKey {
          id
          key
        }
      }
      memoryForDetailHighlights {
        id
        startIndex
        endIndex
        userAnswerId
        mlGenerated
        mlAccepted
        themeKey {
          id
          key
        }
      }
      themeKeys {
        id
        title
        description
        key
        questionId
      }
      userAssessmentCycleId
      assessment {
        displayName
      }
      scoreRanges {
        scoreName
        underperforming
        competent
        accelerated
        reverse
      }
      scores {
        id
        value
        scoreType
        scoreName
        overrideScore
      }
      userAnswers {
        id
        overrideScore
        manualScore {
          id
          scoreName
          value
          userAssessmentId
          questionId
          mlValue
          mlMetadata {
            originalScore
            correction
            matches {
              raw
              clean
              match
            }
          }
          mlTags
          coachManualTags
        }
        answerData
        responseTime
        assessmentQuestion {
          id
          order
          internalName
          questionType
          scoreMax
          scoreMin
          questionData {
            ... on DropdownQuestion {
              prompt
              choices
            }
            ... on DropdownAndTextQuestion {
              prompt
              dropdownChoices: choices
              textDescription
            }
            ... on FlashWordsQuestion {
              wordLists
            }
            ... on FreeResponseQuestion {
              prompt
              time
            }
            ... on ImageThemesQuestion {
              images
            }
            ... on InformationQuestion {
              image
              text
            }
            ... on LikertQuestion {
              prompt
              likertResponses
            }
            ... on MultipleChoiceQuestion {
              prompt
              choices
            }
            ... on MultipleResponseQuestion {
              prompt
              multiResponseChoices
            }
            ... on NumericalScaleQuestion {
              label
              max
              prompt
            }
            ... on PassageQuestion {
              prompt
              passages
            }
            ... on ProverbQuestion {
              proverbList
            }
            ... on TextResponseQuestion {
              prompt
            }
            ... on WordCallbackQuestion {
              prompt
              buckets
            }
          }
        }
      }
    }
  }
`;

export const NewPillarScores = gql`
  query NewPillarScores($userAssessmentCycleId: ID!) {
    newPillarScores(userAssessmentCycleId: $userAssessmentCycleId) {
      id
      pillarName
      value
    }
  }
`;

export const UpdateHighlights = gql`
  mutation updateHighlights($input: UpdateHighlightsInput!) {
    updateHighlights(input: $input) {
      id
      userAnswerId
      themeKeyId
      startIndex
      endIndex
      mlGenerated
      mlConfidence
      mlAccepted
    }
  }
`;

export const UpdateReasoningHighlights = gql`
  mutation updateReasoningHighlights($input: UpdateReasoningHighlightsInput!) {
    updateReasoningHighlights(input: $input) {
      id
      themeKeyId
      startIndex
      endIndex
    }
  }
`;

export const UpdateMemoryBuckets = gql`
  mutation updateMemoryBuckets($input: UpdateMemoryBucketInput!) {
    updateMemoryBuckets(input: $input) {
      confidence
      coach
      ml
    }
  }
`;

export const SaveUserAnswerManualScores = gql`
  mutation saveUserAnswerManualScores($input: UserAnswersManualScoresInput!) {
    saveUserAnswerManualScores(input: $input) {
      id
      scoreName
      value
      userAssessmentId
      questionId
    }
  }
`;

export const SplitTakeHomeMessages = gql`
  mutation SplitTakeHomeMessages($input: SplitTakeHomeMessagesInput!) {
    splitTakeHomeMessages(input: $input) {
      success
    }
  }
`;

export const ResetTakeHomeScoring = gql`
  mutation ResetTakeHomeScoring($input: ResetTakeHomeScoringInput!) {
    resetTakeHomeScoring(input: $input) {
      success
    }
  }
`;

export const GetPillarScores = gql`
  query GetPillarScores($id: ID!) {
    userAssessmentCycle(id: $id) {
      bhiRefactorPillarScores {
        pillarName
        value
      }
    }
  }
`;

export const CalculateAndFinalizePillarScores = gql`
  mutation calculateAndFinalizePillarScores($userAssessmentCycleId: String!) {
    calculateAndFinalizePillarScores(userAssessmentCycleId: $userAssessmentCycleId) {
      id
      pillarName
      value
      userAssessmentCycleId
    }
  }
`;

export const AssessmentCycleNotes = gql`
  query UserAssessmentCycle($id: ID!) {
    userAssessmentCycle(id: $id) {
      notes
    }
  }
`;

export const SaveAssessmentNotes = gql`
  mutation saveAssessmentNotes($input: NotesInput!) {
    saveAssessmentNotes(input: $input) {
      notes
    }
  }
`;

export const SaveInternalNotes = gql`
  mutation SaveInternalNotes($input: InternalNotesMutationInput!) {
    saveInternalNotes(input: $input) {
      notes
    }
  }
`;

export const SaveOverrideUserAnswers = gql`
  mutation saveOverrideUserAnswers($input: OverrideUserAnswersInput!) {
    saveOverrideUserAnswers(input: $input) {
      overrideScore
    }
  }
`;

export const SaveOverrideScores = gql`
  mutation saveOverrideScores($input: OverrideScoresInput!) {
    saveOverrideScores(input: $input) {
      overrideScore
    }
  }
`;

export const ManuallyFlagUserAssessment = gql`
  mutation ManuallyFlagUserAssessment($input: ManuallyFlagUserAssessmentInput!) {
    manuallyFlagUserAssessment(input: $input) {
      id
      validityCode
    }
  }
`;

export const UpdateCodingClinicianReviewed = gql`
  mutation UpdateCodingClinicianReviewed($input: UpdateCodingClinicianReviewedInput!) {
    updateCodingClinicianReviewed(input: $input) {
      id
      isCodingClinicianReviewed
    }
  }
`;
